/**
 * Custom hook for managing irrigation plan steps
 * Handles step CRUD operations, reordering, and bulk operations
 */

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { IrrigationPlanStepFormData } from "@/pages/main/IrrigationPlanWizardPage";
import { stepTanksByStepIdAtom } from "@/store";
import {
  createIrrigationPlanSectorMirrorAtom,
  createIrrigationPlanStepAtom,
  createIrrigationPlanStepTankAtom,
  deleteIrrigationPlanSectorMirrorAtom,
  deleteIrrigationPlanStepAtom,
  deleteIrrigationPlanStepTankAtom,
  swapIrrigationPlanStepsOrdersAtom,
  updateIrrigationPlanStepAtom,
  updateIrrigationPlanStepBatchAtom,
} from "@/store/crud";
import { computeFertigationDuration } from "../../components/IrrigationPlanStepsPanel2/utils/validation";
import { IRRIGATION_DEFAULTS, TEMP_STEP_ID_PREFIX } from "../utils/constants";

// Use any to avoid type mismatches with AUT types
type Sector = any;
type IrrigationPlan = any;

interface UseIrrigationPlanStepsProps {
  /** ID of the plan being edited */
  planId: string;
  /** Available sectors in the project */
  sectors: Sector[];
  /** The current plan data (null in create mode) */
  plan: IrrigationPlan | null;
  /** Mirror data for all steps (extracted from plan.steps.sector_mirrors) */
  mirrors?: Array<{
    id: string;
    source_irrigation_plan_step: string;
    target_sector: { id: string; name: string };
  }>;
}

interface UseIrrigationPlanStepsReturn {
  // State
  planSteps: IrrigationPlanStepFormData[];
  totalDurationSeconds: number;
  savingStepIds: Set<string>;

  // Handlers
  handleAddStep: () => void;
  handleRemoveStep: (stepId: string) => Promise<void>;
  handleBulkRemoveSteps: (stepIds: string[]) => Promise<void>;
  handleSaveStep: (step: IrrigationPlanStepFormData) => Promise<void>;
  handleUpdateStep: (stepId: string, updates: Partial<IrrigationPlanStepFormData>) => void;
  handleMoveStep: (stepId: string, direction: "up" | "down") => Promise<void>;
  handleReorderSteps: (newOrder: IrrigationPlanStepFormData[]) => Promise<void>;
  setPlanSteps: React.Dispatch<React.SetStateAction<IrrigationPlanStepFormData[]>>;
}

/**
 * Hook for managing irrigation plan steps
 */
export function useIrrigationPlanSteps({
  planId,
  sectors,
  plan,
  mirrors,
}: UseIrrigationPlanStepsProps): UseIrrigationPlanStepsReturn {
  // CRUD atoms
  const createPlanStep = useSetAtom(createIrrigationPlanStepAtom);
  const updatePlanStep = useSetAtom(updateIrrigationPlanStepAtom);
  const deletePlanStep = useSetAtom(deleteIrrigationPlanStepAtom);
  const swapPlanStepsOrders = useSetAtom(swapIrrigationPlanStepsOrdersAtom);
  const updatePlanStepBatch = useSetAtom(updateIrrigationPlanStepBatchAtom);
  const createSectorMirror = useSetAtom(createIrrigationPlanSectorMirrorAtom);
  const deleteSectorMirror = useSetAtom(deleteIrrigationPlanSectorMirrorAtom);
  const createPlanStepTank = useSetAtom(createIrrigationPlanStepTankAtom);
  const deletePlanStepTank = useSetAtom(deleteIrrigationPlanStepTankAtom);
  const getStepTanks = useAtomValue(stepTanksByStepIdAtom);

  // Local state
  const [planSteps, setPlanSteps] = useState<IrrigationPlanStepFormData[]>([]);
  const [savingStepIds, setSavingStepIds] = useState<Set<string>>(new Set());

  // Derived: Total duration in seconds
  const totalDurationSeconds = useMemo(() => {
    return planSteps.reduce((total, step) => total + step.durationMinutes * 60, 0);
  }, [planSteps]);

  // Load steps when plan changes
  useEffect(() => {
    if (!plan) {
      setPlanSteps([]);
      return;
    }

    // Convert plan steps to form data
    const formSteps: IrrigationPlanStepFormData[] = (plan.steps || []).map((step: any) => {
      const sectorId = step.sector;
      const sector = sectors.find((s) => s.id === sectorId);
      const sectorName = sector ? sector.name : "Setor não encontrado";

      const tankEntries = (step.irrigation_plan_step_tanks || []).map((t: any) => ({
        projectTankId: t.project_tank.id,
        tankLabel: t.project_tank.tank.label,
        nutrientName: t.project_tank.tank.nutrient.name,
        timeMinutes: t.time_minutes,
      }));

      return {
        id: step.id,
        sectorId,
        sectorName,
        order: step.order,
        durationMinutes: Math.round(step.duration_seconds / 60),
        fertigationStartDelayMinutes: step.fertigation_start_delay_seconds
          ? Math.round(step.fertigation_start_delay_seconds / 60)
          : undefined,
        fertigationDurationMinutes: step.fertigation_duration_seconds
          ? Math.round(step.fertigation_duration_seconds / 60)
          : undefined,
        tankEntries,
      };
    });

    setPlanSteps((prev) => {
      if (
        prev.length === formSteps.length &&
        prev.every(
          (s, i) =>
            s.id === formSteps[i].id &&
            s.durationMinutes === formSteps[i].durationMinutes &&
            s.fertigationStartDelayMinutes === formSteps[i].fertigationStartDelayMinutes &&
            s.fertigationDurationMinutes === formSteps[i].fertigationDurationMinutes &&
            s.order === formSteps[i].order &&
            s.sectorId === formSteps[i].sectorId &&
            JSON.stringify(s.tankEntries) === JSON.stringify(formSteps[i].tankEntries)
        )
      ) {
        return prev;
      }
      return formSteps;
    });
  }, [plan, sectors]);

  /**
   * Add a new step
   */
  const handleAddStep = useCallback(() => {
    const availableSectors = sectors.filter((sector) => !planSteps.some((step) => step.sectorId === sector.id));

    if (availableSectors.length === 0) {
      return; // No more sectors available
    }

    const newStep: IrrigationPlanStepFormData = {
      id: `${TEMP_STEP_ID_PREFIX}${Date.now()}`,
      sectorId: availableSectors[0].id,
      sectorName: availableSectors[0].name,
      order: planSteps.length + 1,
      durationMinutes: IRRIGATION_DEFAULTS.DEFAULT_DURATION_MINUTES,
      fertigationStartDelayMinutes: plan?.fertigation_enabled ? 2 : undefined,
      fertigationDurationMinutes: undefined,
      tankEntries: [],
    };

    setPlanSteps((prev) => [...prev, newStep]);
  }, [planSteps, sectors, plan]);

  /**
   * Bulk remove steps
   */
  const handleBulkRemoveSteps = async (stepIds: string[]) => {
    try {
      // Separate new steps from existing steps
      const existingStepIds = stepIds.filter((id) => !id.startsWith(TEMP_STEP_ID_PREFIX));

      // Delete existing steps from API
      for (const stepId of existingStepIds) {
        const result = await deletePlanStep(stepId);
        if (!result.success) {
          // Error handling: Rollback local state changes
          // For now, just stop and let the toast show the error
          return;
        }
      }

      // Calculate updated steps with new order after removing all specified steps
      const remainingSteps = planSteps.filter((step) => !stepIds.includes(step.id));
      const updatedSteps = remainingSteps.map((step, index) => ({
        ...step,
        order: index + 1,
      }));

      // Update local state
      setPlanSteps(updatedSteps);

      // Persist order changes for existing steps that had their order changed
      const stepsWithChangedOrder = updatedSteps.filter((updatedStep) => {
        const originalStep = remainingSteps.find((s) => s.id === updatedStep.id);
        return (
          originalStep && originalStep.order !== updatedStep.order && !updatedStep.id.startsWith(TEMP_STEP_ID_PREFIX)
        );
      });

      // Update order for existing steps that had their order changed
      for (const step of stepsWithChangedOrder) {
        const stepData = {
          order: step.order,
        };

        await updatePlanStep({ id: step.id, data: stepData });
      }

      // Data is already refetched by the CRUD operations
      // No need to call refetchData again
    } catch (error) {
      // Error is already handled by withOperationHandlingAtom
    }
  };

  /**
   * Remove a single step (wrapper around bulk remove)
   */
  const handleRemoveStep = useCallback(
    async (stepId: string) => {
      await handleBulkRemoveSteps([stepId]);
    },
    [handleBulkRemoveSteps]
  );

  /**
   * Save a step (create or update)
   */
  const handleSaveStep = async (step: IrrigationPlanStepFormData) => {
    const isNewStep = step.id.startsWith(TEMP_STEP_ID_PREFIX);

    // Track loading state
    setSavingStepIds((prev) => new Set(prev).add(step.id));

    const syncStepTanks = async (stepId: string) => {
      const existingTanks = getStepTanks(stepId) || [];
      const existingMap = new Map(existingTanks.map((t: any) => [t.project_tank.id, t]));
      const newMap = new Map(step.tankEntries.map((t) => [t.projectTankId, t]));

      // Added: in new but not in existing
      for (const [projectTankId, entry] of newMap) {
        if (!existingMap.has(projectTankId)) {
          await createPlanStepTank({
            irrigation_plan_step: stepId,
            project_tank: projectTankId,
            time_minutes: entry.timeMinutes,
          });
        }
      }

      // Removed: in existing but not in new
      for (const [projectTankId, existing] of existingMap) {
        if (!newMap.has(projectTankId)) {
          await deletePlanStepTank(existing.id);
        }
      }

      // Changed: in both but time_minutes differs
      for (const [projectTankId, entry] of newMap) {
        const existing = existingMap.get(projectTankId);
        if (existing && existing.time_minutes !== entry.timeMinutes) {
          await deletePlanStepTank(existing.id);
          await createPlanStepTank({
            irrigation_plan_step: stepId,
            project_tank: projectTankId,
            time_minutes: entry.timeMinutes,
          });
        }
      }
    };

    const fertigationDurationSeconds =
      step.tankEntries.length > 0
        ? computeFertigationDuration(step.tankEntries) * 60
        : step.fertigationDurationMinutes
          ? step.fertigationDurationMinutes * 60
          : null;

    try {
      if (isNewStep) {
        // CREATE: New step with temporary ID
        if (!planId) {
          throw new Error("No plan ID available for creating step");
        }

        const stepData = {
          irrigation_plan: planId,
          sector: step.sectorId,
          order: step.order,
          duration_seconds: step.durationMinutes * 60,
          fertigation_start_delay_seconds: step.fertigationStartDelayMinutes
            ? step.fertigationStartDelayMinutes * 60
            : null,
          fertigation_duration_seconds: fertigationDurationSeconds,
        };

        const result = await createPlanStep(stepData);
        if (result.success && result.data) {
          const newStep = result.data as { id: string };
          setPlanSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, id: newStep.id } : s)));
          await syncStepTanks(newStep.id);
        }
      } else {
        // UPDATE: Existing step with real ID
        const stepData = {
          sector: step.sectorId,
          order: step.order,
          duration_seconds: step.durationMinutes * 60,
          fertigation_start_delay_seconds: step.fertigationStartDelayMinutes
            ? step.fertigationStartDelayMinutes * 60
            : null,
          fertigation_duration_seconds: fertigationDurationSeconds,
        };

        await updatePlanStep({ id: step.id, data: stepData });
        await syncStepTanks(step.id);
      }

      // Sync mirror targets (sequential: step saved first, then mirrors)
      if (!isNewStep && step.mirrorTargetIds !== undefined) {
        const existingMirrors = mirrors?.filter((m) => m.source_irrigation_plan_step === step.id) || [];
        const existingTargetIds = new Set(existingMirrors.map((m) => m.target_sector.id));
        const newTargetIds = new Set(step.mirrorTargetIds);

        const toAdd = step.mirrorTargetIds.filter((id) => !existingTargetIds.has(id));
        const toRemove = existingMirrors.filter((m) => !newTargetIds.has(m.target_sector.id));

        for (const targetId of toAdd) {
          try {
            await createSectorMirror({
              source_irrigation_plan_step: step.id,
              target_sector: targetId,
            });
          } catch (error) {
            console.error(`Failed to create mirror for target ${targetId}:`, error);
          }
        }

        for (const mirror of toRemove) {
          try {
            await deleteSectorMirror(mirror.id);
          } catch (error) {
            console.error(`Failed to delete mirror ${mirror.id}:`, error);
          }
        }
      }
    } catch (error) {
      // Error is already handled by withOperationHandlingAtom
    } finally {
      setSavingStepIds((prev) => {
        const next = new Set(prev);
        next.delete(step.id);
        return next;
      });
    }
  };

  /**
   * Update a step locally (optimistic update)
   */
  const handleUpdateStep = useCallback((stepId: string, updates: Partial<IrrigationPlanStepFormData>) => {
    setPlanSteps((prev) => prev.map((step) => (step.id === stepId ? { ...step, ...updates } : step)));
  }, []);

  /**
   * Move a step up or down
   */
  const handleMoveStep = async (stepId: string, direction: "up" | "down") => {
    const currentIndex = planSteps.findIndex((step) => step.id === stepId);
    if ((direction === "up" && currentIndex === 0) || (direction === "down" && currentIndex === planSteps.length - 1)) {
      return;
    }

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    const currentStep = planSteps[currentIndex];
    const targetStep = planSteps[targetIndex];

    // Check if both steps are existing (not new) steps
    const isCurrentStepNew = currentStep.id.startsWith(TEMP_STEP_ID_PREFIX);
    const isTargetStepNew = targetStep.id.startsWith(TEMP_STEP_ID_PREFIX);

    if (!isCurrentStepNew && !isTargetStepNew) {
      // Both steps are existing - use the swap operation
      try {
        const step1 = { id: currentStep.id, order: currentStep.order };
        const step2 = { id: targetStep.id, order: targetStep.order };

        await swapPlanStepsOrders(step1, step2);
        // Data is already refetched by the swap operation
        // No need to call refetchData again
      } catch (error) {
        // Error is already handled by withOperationHandlingAtom
        return;
      }
    }

    // Update local state for immediate UI feedback
    const newSteps = [...planSteps];

    // Swap steps
    [newSteps[currentIndex], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[currentIndex]];

    // Update order numbers
    newSteps.forEach((step, index) => {
      step.order = index + 1;
    });

    setPlanSteps(newSteps);
  };

  /**
   * Reorder steps (drag and drop)
   */
  const handleReorderSteps = async (newOrder: IrrigationPlanStepFormData[]) => {
    // Update the order numbers to match the new sequence
    const reorderedSteps = newOrder.map((step, index) => ({
      ...step,
      order: index + 1,
    }));

    setPlanSteps(reorderedSteps);
    await updatePlanStepBatch(reorderedSteps.map((step) => ({ id: step.id, order: step.order })));
  };

  return {
    // State
    planSteps,
    totalDurationSeconds,
    savingStepIds,

    // Handlers
    handleAddStep,
    handleRemoveStep,
    handleBulkRemoveSteps,
    handleSaveStep,
    handleUpdateStep,
    handleMoveStep,
    handleReorderSteps,
    setPlanSteps,
  };
}
